import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Flame, Bell, BellRing, Pencil, Check, Trophy, Gift } from "lucide-react";
import api from "../lib/api";
import { enableWeeklyReminders, disableWeeklyReminders, pushSupported } from "../lib/push";
import { Button } from "./ui/button";

function Ring({ value }) {
  const r = 34, c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 84 84" className="w-24 h-24 -rotate-90">
      <circle cx="42" cy="42" r={r} stroke="rgba(255,255,255,0.12)" strokeWidth="8" fill="none" />
      <motion.circle cx="42" cy="42" r={r} stroke="#f59e0b" strokeWidth="8" fill="none" strokeLinecap="round" strokeDasharray={c}
        initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c - (c * value) / 100 }} transition={{ duration: 1, ease: "easeOut" }} />
    </svg>
  );
}

export function StreakCard({ compact = false }) {
  const [s, setS] = useState(null);
  const [editing, setEditing] = useState(false);
  const [goal, setGoal] = useState(100);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/me/streak").then(({ data }) => {
    setS(data); setGoal(data.weekly_goal);
    if (data.reward?.just_earned) toast.success("7-day streak complete! You earned 1 free Mock Oral Board — it's ready to use.", { duration: 8000 });
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveGoal = async () => {
    try { const { data } = await api.put("/me/streak/goal", { weekly_goal: Number(goal) }); setS((x) => ({ ...x, weekly_goal: data.weekly_goal, week_progress: Math.min(100, Math.round((x.week_questions / data.weekly_goal) * 100)) })); setEditing(false); toast.success(`Weekly goal set to ${data.weekly_goal} questions`); }
    catch { toast.error("Could not save goal."); }
  };

  const toggleReminders = async () => {
    setBusy(true);
    try {
      if (s.push_enabled) { await disableWeeklyReminders(); toast.success("Weekly reminders off."); }
      else { await enableWeeklyReminders(); toast.success("Weekly reminders on — we'll nudge you if you go a week without training."); }
      await load();
    } catch (e) {
      toast.error(e.message === "denied" ? "Notifications were blocked in your browser." : e.message === "unsupported" ? "This browser doesn't support push notifications." : "Could not enable reminders.");
    } finally { setBusy(false); }
  };

  if (!s) return null;
  return (
    <div className="space-y-4" data-testid="streak-card">
      {s.welcome_back && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="bg-amber-50 border border-amber-300 rounded-2xl px-5 py-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid="welcome-back-banner">
          <div>
            <p className="font-heading text-xl text-slate-900">We missed you — it's been {s.days_since_active} days.</p>
            <p className="text-sm text-amber-800">Your streak reset, but the badge didn't move. A 10-question set gets you rolling again.</p>
          </div>
        </motion.div>
      )}
      <div className="bg-slate-900 text-white rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-6">
        <div className="flex items-center gap-5">
          <div className="relative">
            <Ring value={s.week_progress} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-heading text-2xl font-bold leading-none" data-testid="week-progress">{s.week_progress}%</span>
              <span className="text-[9px] uppercase tracking-widest text-slate-400">weekly</span>
            </div>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Weekly goal</p>
            {editing ? (
              <div className="flex items-center gap-2 mt-1">
                <input type="number" min="10" max="1000" step="10" value={goal} onChange={(e) => setGoal(e.target.value)} className="w-24 bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-white font-heading text-lg" data-testid="weekly-goal-input" />
                <Button size="sm" onClick={saveGoal} className="bg-amber-500 text-slate-950 hover:bg-amber-400 h-8" data-testid="weekly-goal-save"><Check className="w-4 h-4" /></Button>
              </div>
            ) : (
              <button onClick={() => setEditing(true)} className="flex items-center gap-2 mt-1 group" data-testid="weekly-goal-edit">
                <span className="font-heading text-2xl"><span data-testid="week-questions">{s.week_questions}</span> <span className="text-slate-500">/ {s.weekly_goal} questions</span></span>
                <Pencil className="w-3.5 h-3.5 text-slate-500 group-hover:text-white" />
              </button>
            )}
            <p className="text-xs text-slate-400 mt-1">{s.week_boards} oral board{s.week_boards === 1 ? "" : "s"} this week · last week {s.prev_week_questions} questions</p>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-2 gap-4 md:border-l md:border-slate-800 md:pl-6">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1"><Flame className="w-3.5 h-3.5 text-primary" /> Streak</p>
            <p className="font-heading text-3xl font-bold mt-1" data-testid="current-streak">{s.current_streak} <span className="text-base text-slate-500 font-body">day{s.current_streak === 1 ? "" : "s"}</span></p>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1"><Trophy className="w-3.5 h-3.5 text-amber-400" /> Longest</p>
            <p className="font-heading text-3xl font-bold mt-1" data-testid="longest-streak">{s.longest_streak} <span className="text-base text-slate-500 font-body">days</span></p>
          </div>
          {s.reward?.eligible && (
            <div className="col-span-2" data-testid="streak-reward">
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1"><Gift className="w-3.5 h-3.5 text-amber-400" /> Current streak: {s.reward.progress} / {s.reward.target} days → free Mock Oral Board</span>
                <span className="text-slate-400" data-testid="streak-reward-progress">{s.reward.days_to_next} day{s.reward.days_to_next === 1 ? "" : "s"} to go{s.reward.earned_total > 0 ? ` · ${s.reward.earned_total} earned` : ""}</span>
              </div>
              <div className="mt-1.5 flex gap-1">{Array.from({ length: s.reward.target }).map((_, i) => <span key={i} className={`h-1.5 flex-1 rounded-full ${i < s.reward.progress ? "bg-amber-400" : "bg-slate-700"}`} />)}</div>
            </div>
          )}
        </div>
        <div className="flex flex-col items-start md:items-end gap-2">
            {!compact && <div className="flex gap-1" data-testid="streak-calendar">
              {s.calendar.slice(-14).map((d) => (
                <span key={d.date} title={`${d.date}: ${d.questions} questions`} className={`w-3 h-6 rounded-sm ${d.questions + d.boards > 0 ? "bg-amber-400" : "bg-slate-700"}`} />
              ))}
            </div>}
            {pushSupported() && (
              <Button variant="ghost" size="sm" onClick={toggleReminders} disabled={busy} title={s.push_enabled ? "Weekly reminders are on — tap to turn off" : "Turn on weekly reminders"} className={`h-8 ${s.push_enabled ? "text-amber-300 hover:text-amber-200 hover:bg-white/10" : "text-slate-300 hover:text-white hover:bg-white/10"}`} data-testid="reminders-toggle" data-state={s.push_enabled ? "on" : "off"}>
                {s.push_enabled ? <><BellRing className="w-4 h-4 mr-1.5" /> Reminders on</> : <><Bell className="w-4 h-4 mr-1.5" /> Remind me weekly</>}
              </Button>
            )}
          </div>
      </div>
      {!compact && s.recap.questions > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="weekly-recap">
          {[["Last 7 days", `${s.recap.questions} q`], ["Accuracy", s.recap.accuracy != null ? `${s.recap.accuracy}%` : "—"], ["Boards", s.recap.boards], ["Active days", `${s.recap.active_days}/7`]].map(([l, v]) => (
            <div key={l}><p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{l}</p><p className="font-heading text-2xl text-slate-900">{v}</p></div>
          ))}
        </div>
      )}
    </div>
  );
}
